export const ANIMAL_IDS = [
  "fox",
  "owl",
  "bear",
  "hare",
  "wolf",
  "turtle",
  "deer",
  "cat",
  "frog",
  "hedge",
  "mouse",
  "raccoon",
] as const;

export type AnimalId = (typeof ANIMAL_IDS)[number];

/*
 * The catalogues hold ids and measurements, never names: a name is copy, and
 * copy is what the consumer passes in its own language. Every component that
 * shows one takes a `…Names` map keyed by these ids.
 */

export interface AnimalDefinition {
  id: AnimalId;
  naturalColour: string;
  figure: {
    width: number;
    headWidth: number;
    headHeight: number;
    headCenterX: number;
    headTop: number;
  };
}

export const ANIMALS: readonly AnimalDefinition[] = [
  {
    id: "fox",
    naturalColour: "hsl(var(--animal-fox))",
    figure: {
      width: 66,
      headWidth: 44.67,
      headHeight: 25.33,
      headCenterX: 41.08,
      headTop: 10.33,
    },
  },
  {
    id: "owl",
    naturalColour: "hsl(var(--animal-owl))",
    figure: {
      width: 61.33,
      headWidth: 41,
      headHeight: 27.67,
      headCenterX: 34.52,
      headTop: 6,
    },
  },
  {
    id: "bear",
    naturalColour: "hsl(var(--animal-bear))",
    figure: {
      width: 56.33,
      headWidth: 38,
      headHeight: 39,
      headCenterX: 28.16,
      headTop: 5.67,
    },
  },
  {
    id: "hare",
    naturalColour: "hsl(var(--animal-hare))",
    figure: {
      width: 48.33,
      headWidth: 30.67,
      headHeight: 18,
      headCenterX: 27.35,
      headTop: 24,
    },
  },
  {
    id: "wolf",
    naturalColour: "hsl(var(--animal-wolf))",
    figure: {
      width: 64,
      headWidth: 43.33,
      headHeight: 26.67,
      headCenterX: 39.76,
      headTop: 9.33,
    },
  },
  {
    id: "turtle",
    naturalColour: "hsl(var(--animal-turtle))",
    figure: {
      width: 70.67,
      headWidth: 36,
      headHeight: 22,
      headCenterX: 38,
      headTop: 23,
    },
  },
  {
    id: "deer",
    naturalColour: "hsl(var(--animal-deer))",
    figure: {
      width: 61,
      headWidth: 35,
      headHeight: 29,
      headCenterX: 31,
      headTop: 5,
    },
  },
  {
    id: "cat",
    naturalColour: "hsl(var(--animal-cat))",
    figure: {
      width: 53.67,
      headWidth: 36,
      headHeight: 28,
      headCenterX: 27,
      headTop: 8,
    },
  },
  {
    id: "frog",
    naturalColour: "hsl(var(--animal-frog))",
    figure: {
      width: 60,
      headWidth: 42,
      headHeight: 24,
      headCenterX: 30,
      headTop: 14,
    },
  },
  {
    id: "hedge",
    naturalColour: "hsl(var(--animal-hedge))",
    figure: {
      width: 63.33,
      headWidth: 37,
      headHeight: 27,
      headCenterX: 32,
      headTop: 15,
    },
  },
  {
    id: "mouse",
    naturalColour: "hsl(var(--animal-mouse))",
    figure: {
      width: 48.67,
      headWidth: 32,
      headHeight: 26,
      headCenterX: 25,
      headTop: 12,
    },
  },
  {
    id: "raccoon",
    naturalColour: "hsl(var(--animal-raccoon))",
    figure: {
      width: 60.67,
      headWidth: 40,
      headHeight: 28,
      headCenterX: 31,
      headTop: 8,
    },
  },
] as const;

export const COLOURS = [
  ["rust", "hsl(var(--identity-rust))"],
  ["clay", "hsl(var(--identity-clay))"],
  ["honey", "hsl(var(--identity-honey))"],
  ["mustard", "hsl(var(--identity-mustard))"],
  ["olive", "hsl(var(--identity-olive))"],
  ["moss", "hsl(var(--identity-moss))"],
  ["mint", "hsl(var(--identity-mint))"],
  ["teal", "hsl(var(--identity-teal))"],
  ["lagoon", "hsl(var(--identity-lagoon))"],
  ["sky", "hsl(var(--identity-sky))"],
  ["frost", "hsl(var(--identity-frost))"],
  ["steel", "hsl(var(--identity-steel))"],
  ["indigo", "hsl(var(--identity-indigo))"],
  ["night", "hsl(var(--identity-night))"],
  ["lilac", "hsl(var(--identity-lilac))"],
  ["ash", "hsl(var(--identity-ash))"],
  ["plum", "hsl(var(--identity-plum))"],
  ["orchid", "hsl(var(--identity-orchid))"],
  ["rose", "hsl(var(--identity-rose))"],
  ["brick", "hsl(var(--identity-brick))"],
] as const;

export type ColourId = (typeof COLOURS)[number][0];

export const LEVELS = [
  { id: 1, hours: 0 },
  { id: 2, hours: 2 },
  { id: 3, hours: 8 },
  { id: 4, hours: 20 },
  { id: 5, hours: 45 },
  { id: 6, hours: 90 },
  { id: 7, hours: 160 },
  { id: 8, hours: 300 },
] as const;

export const BADGES = [
  { id: "firstnight" },
  { id: "invite" },
  { id: "host" },
  { id: "feedback" },
  { id: "full" },
  { id: "told" },
  { id: "owl" },
] as const;

export type BadgeId = (typeof BADGES)[number]["id"];
export type PlaceSlot = "near" | "side" | "air";

export const PLACE = [
  { id: "mug", slot: "near", level: 1 },
  { id: "lantern", slot: "near", level: 5 },
  { id: "stick", slot: "near", level: 4 },
  { id: "guitar", slot: "near", level: 8 },
  { id: "marsh", slot: "near", badge: "full" },
  { id: "book", slot: "near", badge: "told" },
  { id: "notes", slot: "near", badge: "feedback" },
  { id: "pack", slot: "side", level: 2 },
  { id: "logs", slot: "side", level: 3 },
  { id: "kettle", slot: "side", badge: "host" },
  { id: "stump", slot: "side", badge: "invite" },
  { id: "lamp", slot: "air", badge: "owl" },
  { id: "sparks", slot: "air", level: 6 },
  { id: "moths", slot: "air", level: 7 },
] as const satisfies readonly {
  id: string;
  slot: PlaceSlot;
  level?: number;
  badge?: BadgeId;
}[];

export type PlaceItemId = (typeof PLACE)[number]["id"];

export const VOICES = [
  { id: "raven" },
  { id: "even" },
  { id: "herald" },
  { id: "whisper" },
] as const;

export type VoiceId = (typeof VOICES)[number]["id"];

export type LevelId = (typeof LEVELS)[number]["id"];

/** A name and a line under it, as a badge or a voice is shown. */
export interface NamedText {
  name: string;
  description: string;
}

export type AnimalNames = Readonly<Record<AnimalId, string>>;
export type ColourNames = Readonly<Record<ColourId, string>>;
export type LevelNames = Readonly<Record<LevelId, string>>;
export type PlaceNames = Readonly<Record<PlaceItemId, string>>;
export type BadgeTexts = Readonly<Record<BadgeId, NamedText>>;
export type VoiceTexts = Readonly<Record<VoiceId, NamedText>>;

export function colourValue(id: ColourId): string {
  return COLOURS.find(([candidate]) => candidate === id)?.[1] ?? COLOURS[0][1];
}

export function deterministicCampIdentity(peerId: string): {
  animal: AnimalId;
  colour: ColourId;
} {
  let hash = 0;
  for (let index = 0; index < peerId.length; index += 1)
    hash = (hash << 5) - hash + peerId.charCodeAt(index);
  const positive = Math.abs(hash);
  const animal = ANIMAL_IDS[positive % ANIMAL_IDS.length] ?? "fox";
  const colour = COLOURS[positive % COLOURS.length]?.[0] ?? "ash";
  return { animal, colour };
}
